import { z } from 'zod';

/**
 * Contrats des notifications push (spec 13, ADR 0021). Aucun DTO n'expose le
 * token FCM (capacité d'envoi vers l'appareil, spec 13 §10) : le front reçoit
 * seulement un `tokenHash` (sha256 tronqué) pour reconnaître « cet appareil » en
 * hashant son propre token. Les préférences sont exprimées en « recevoir »,
 * l'API stocke l'inverse (`*PushOptOutAt`) — patron `EmailPreferencesDto`.
 */

/** Plateforme d'un appareil abonné (enum Prisma `PushPlatform`). */
export const PUSH_PLATFORMS = ['web', 'android', 'ios'] as const;
export type PushPlatform = (typeof PUSH_PLATFORMS)[number];

/** Catégories de push (spec 13 §3/§6). v1 : seule `invites` a un producteur. */
export const PUSH_CATEGORIES = ['invites', 'daily', 'news'] as const;
export type PushCategory = (typeof PUSH_CATEGORIES)[number];

/** Borne généreuse : un token FCM fait ~150–350 car., mais peut évoluer. */
export const PUSH_TOKEN_MAX = 4096;
/** Quota d'appareils par compte (spec 13 §2) : au-delà, le plus ancien est purgé. */
export const PUSH_DEVICES_MAX = 10;

const platformField = z.enum(PUSH_PLATFORMS);

/**
 * Enregistrement/revalidation d'un appareil (spec 13 §5) : upsert idempotent par
 * `token`. `userAgent` optionnel (sinon l'API prend l'en-tête de la requête).
 */
export const PushRegisterSchema = z
  .object({
    token: z.string().trim().min(1).max(PUSH_TOKEN_MAX),
    platform: platformField,
    userAgent: z.string().trim().max(400).optional(),
  })
  .strict();
export type PushRegisterDto = z.infer<typeof PushRegisterSchema>;

/**
 * Préférences push du user (spec 13 §3) exprimées en « recevoir » (true = je
 * reçoits cette catégorie). Défaut activé une fois qu'un appareil est abonné.
 */
export interface PushPreferencesDto {
  invites: boolean;
  daily: boolean;
  news: boolean;
}

/** Mise à jour partielle des préférences — au moins une clé requise. */
export const PushPreferencesUpdateSchema = z
  .object({
    invites: z.boolean().optional(),
    daily: z.boolean().optional(),
    news: z.boolean().optional(),
  })
  .strict()
  .refine((v) => v.invites !== undefined || v.daily !== undefined || v.news !== undefined, {
    message: 'au moins une préférence est requise',
  });
export type PushPreferencesUpdateDto = z.infer<typeof PushPreferencesUpdateSchema>;

/**
 * Appareil renvoyé au client (spec 13 §4). JAMAIS le token : `tokenHash`
 * (sha256 tronqué) permet au front d'identifier « cet appareil ».
 */
export interface PushDeviceDto {
  id: string;
  label: string;
  platform: PushPlatform;
  createdAt: string;
  lastSeenAt: string;
  tokenHash: string;
}

/**
 * Message push (spec 13 §4) — contrat PARTAGÉ serveur ↔ service worker : le
 * serveur sérialise (payload data-only), le SW parse (tolérant). `url` =
 * deep-link (ex. `/motus/m/<id>`). Jamais de secret de jeu (règle d'or n°1).
 */
export const PushMessageSchema = z
  .object({
    title: z.string().min(1).max(200),
    body: z.string().min(1).max(1000),
    url: z.string().min(1).max(1000),
    tag: z.string().max(200).optional(),
    icon: z.string().max(1000).optional(),
  })
  .strict();
export type PushMessage = z.infer<typeof PushMessageSchema>;

/**
 * Config web Firebase PUBLIQUE (spec 13 §5) servie par `GET /push/config` — pas
 * de `VITE_*` (pas de rebuild front en cas de rotation). Publique par
 * conception : le secret, c'est le service account serveur (spec 13 §8).
 */
export interface PushConfigDto {
  apiKey: string;
  projectId: string;
  messagingSenderId: string;
  appId: string;
  vapidKey: string;
}
